import { toaster } from '@/components/ui/toaster';
import { brandTermsQueryKey } from '@/hooks/queries/use-brand-terms-query';
import api from '@/libs/axiosInstance';
import type {
  ApiResponse,
  BrandTermDocument,
  BrandTermsSettings,
  PayloadLocale,
  PublishBrandTermsRequest,
  SaveBrandTermsDraftRequest,
} from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export const useSaveBrandTermsDraft = (brandId: string, locale: PayloadLocale) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation<BrandTermDocument, AxiosError<ApiResponse>, SaveBrandTermsDraftRequest>({
    mutationFn: async (input) => {
      const { data } = await api.put<ApiResponse<BrandTermDocument>>(
        '/dashboard/brand-terms/draft',
        input
      );
      if (!data.success || !data.data) throw new Error(data.message);
      return data.data;
    },
    onSuccess: (draft) => {
      queryClient.setQueryData<BrandTermsSettings>(
        brandTermsQueryKey(brandId, locale),
        (current) => (current ? { ...current, draft } : current)
      );
      toaster.success({
        title: t('settings.brandTerms.savedTitle'),
        description: t('settings.brandTerms.savedDescription'),
      });
    },
    onError: () =>
      toaster.error({
        title: t('settings.brandTerms.saveErrorTitle'),
        description: t('settings.brandTerms.saveErrorDescription'),
      }),
  });
};

export const usePublishBrandTerms = (brandId: string, locale: PayloadLocale) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation<BrandTermDocument, AxiosError<ApiResponse>, PublishBrandTermsRequest>({
    mutationFn: async (input) => {
      const { data } = await api.post<ApiResponse<BrandTermDocument>>(
        '/dashboard/brand-terms/publish',
        input
      );
      if (!data.success || !data.data) throw new Error(data.message);
      return data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: brandTermsQueryKey(brandId, locale) });
      toaster.success({
        title: t('settings.brandTerms.publishedTitle'),
        description: t('settings.brandTerms.publishedDescription'),
      });
    },
    onError: () =>
      toaster.error({
        title: t('settings.brandTerms.publishErrorTitle'),
        description: t('settings.brandTerms.publishErrorDescription'),
      }),
  });
};
